import {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
  KeyboardEventHandler,
} from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Cookies from "js-cookie";

import { CommonButton } from "@/components/common/buttons/CommonButton";
import Title from "@/components/molecules/Title";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import { AutoCompleteSearch } from "@/components/molecules/AutoCompleteSearch";
import {
  ChargePlansConstants,
  chargePlansEventPropGetter,
  IGNORE_COLOR,
  SOC_COLORS,
} from "./ChargePlansConstants";
import { Office, ONLY_CONTRACT } from "@/models/office";
import {
  searchChargePlans,
  SearchChargePlansValidator,
  ChargePlan,
  ChargePlanCalendarEvent,
  Palet,
} from "@/models/chargePlans";
import { getOfficesAPI, getLoginUserAPI } from "@/util/api/API";
import {
  cancelChargePlansAPI,
  getChargePlansAPI,
} from "@/util/api/chargePlans";
import { getJSTCurrentTime, getJSTTime } from "@/util/datetime";
import { SINGLE_OFFICE_ID } from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import { GX_ADMIN } from "@/models/auth";
import {
  GxBigCalendar,
  GxBigCalendarHeader,
} from "@/components/molecules/GxBigCalendar";

export const ChargePlans = (): JSX.Element => {
  const [chargePlans, setChargePlans] = useState<ChargePlan<Palet.Plan>>();
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeSelected, setOfficeSelected] = useState<Office>();
  const [targetDateSelected, setTargetDateSelected] = useState<string>(
    getJSTCurrentTime("date")
  );
  const [searchData, setSearchData] = useState<searchChargePlans>({
    targetDate: getJSTCurrentTime("date"),
  });
  const [searchDataValidator, setSearchDataValidator] =
    useState<SearchChargePlansValidator>({});
  const [initSearchFlag, setInitSearchFlag] = useState<boolean>(false);
  const [isGxAdmin, setIsGxAdmin] = useState<boolean>(false);

  const [showSoC, setShowSoC] = useState<boolean>(false);

  const socResourceIdInterval = 0.5;
  const socResourceStyle = {
    width: "48px",
    flex: "none",
    minWidth: "48px",
    padding: 0,
    margin: 0,
  };

  const filteredEvents = useMemo(() => {
    if (!chargePlans?.chargePlanCalendarEvent) return [];
    return chargePlans.chargePlanCalendarEvent.filter((event) => {
      if (SOC_COLORS.includes(event.color)) {
        return showSoC;
      }
      return true;
    });
  }, [chargePlans, showSoC]);

  const handleSearchData = useCallback(
    async (search: searchChargePlans) => {
      let haveError = false;
      const searchDataValidatorTemp: SearchChargePlansValidator = {};
      if (!officeSelected) {
        haveError = true;
        searchDataValidatorTemp.officeId = true;
      }
      if (haveError) {
        return setSearchDataValidator({
          ...searchDataValidatorTemp,
        });
      }

      try {
        const res = await getChargePlansAPI(search);
        if (!res.data.chargePlanCalendarResource.length) {
          setChargePlans(undefined);
          return;
        }

        // 普通充電器の充電器状態実績, 充電器給電実績イベントは削除する
        res.data.chargePlanCalendarEvent =
          res.data.chargePlanCalendarEvent.filter(
            ({ color }) => !IGNORE_COLOR.includes(color)
          ).map((event) => {
            if (SOC_COLORS.includes(event.color)) {
              event.titleName = event.title;
              event.title = "";
              event.resourceId += socResourceIdInterval;
            }
            return event;
          });

        // SoC履歴用のリソースを追加する
        res.data.chargePlanCalendarResource =
          res.data.chargePlanCalendarResource.flatMap((resource) => {
            return [
              resource,
              {
                ...resource,
                resourceId:
                  resource.resourceId + socResourceIdInterval,
                headerCode: "SoC",
              },
            ];
          });

        for (const event of res.data.chargePlanCalendarEvent) {
          event.start = new Date(event.start.toString().replace("-", "/"));
          event.end = new Date(event.end.toString().replace("-", "/"));

          if (!SOC_COLORS.includes(event.color)) { // SoCは1コマ30分なので終了時間をずらさない
            // OSSバグ対策
            // startとendどちらも0:00だった場合、表示ができなくなるため
            event.end.setMilliseconds(1);
          }

          event.officeId = search.officeId;
          event.targetDate = search.targetDate;
          event.vehicleCode = res.data.chargePlanCalendarResource.find(
            (r) => r.resourceId === event.resourceId
          )?.headerCode;
        }
        setChargePlans(res.data);
      } catch (err) {
        console.log("==Error when getChargePlansAPI==", err);
      }
    },
    [officeSelected]
  );

  const getOffices = async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const found = res.data.find(
        (d) => d.id === Number(Cookies.get(SINGLE_OFFICE_ID))
      );
      if (found) {
        setOfficeSelected(found);
        setSearchData({
          ...searchData,
          officeId: found.id,
        });
        setInitSearchFlag(true);
      }
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  };

  // Handle event reset search data
  const onClickResetSearchData = () => {
    setOfficeSelected(undefined);
    setSearchData({ targetDate: getJSTCurrentTime("date") });
    setSearchDataValidator({});
    setTargetDateSelected(getJSTCurrentTime("date"));
  };

  const onChangeOffice = useCallback(
    (office: Office | undefined) => {
      setSearchData({
        ...searchData,
        officeId: office?.id,
      });
      setSearchDataValidator({
        ...searchDataValidator,
        officeId: false,
      });
      setOfficeSelected(office);
      Cookies.set(SINGLE_OFFICE_ID, office?.id.toString() || "");
    },
    [searchData, searchDataValidator]
  );

  const onChangeVehicleCode = useCallback(
    (code: string) => {
      setSearchData({
        ...searchData,
        vehicleCode: code,
      });
    },
    [searchData]
  );

  const onChangeDate = useCallback(
    (date: string) => {
      setSearchData({
        ...searchData,
        targetDate: date,
      });
      setTargetDateSelected(date);
      handleSearchData({
        ...searchData,
        targetDate: date,
      });
    },
    [searchData, handleSearchData]
  );

  // 現在時刻以降の充電イベントクリック時にキャンセルする。
  // 事前に確認ダイアログを表示する
  const handleSelectEvent = useCallback(
    (event: ChargePlanCalendarEvent<Palet.Plan>) => {
      // 充電イベント以外は何もしない
      // emp はキャンセルさせない
      if (!["quick_other", "standard", "quick"].includes(event.color)) {
        return;
      }
      if (!(event.end instanceof Date) || !(event.start instanceof Date)) {
        return;
      }

      // すでに終了した予定はキャンセル対象外
      const now = new Date();
      if (event.end < now) {
        return;
      }

      // 開始時刻を過ぎているイベントの場合は現在時刻のあるbin 以降のみキャンセルする
      const start = event.start.toTimeString().substring(0, 5);
      const end = event.end.toTimeString().substring(0, 5);

      const message =
        `車両番号: ${event.vehicleCode}\n充電器: ${event.title || ""
        }\n時間: ${start} - ${end}\n\n` +
        "この充電予定を取り消します。よろしければOKをクリックしてください\n" +
        "※取り消した後も同様の予定が自動的に計画されることがあります";

      if (window.confirm(message)) {
        const now = new Date(); // ダイアログが数時間放置されるレアケースを考慮し、改めて現在時刻をとる

        // HH:MM 文字列から bin を求める関数を定義
        const toBin = (hh_mm: string): number =>
          parseInt(hh_mm.substring(0, 2)) * 2 +
          ~~(parseInt(hh_mm.substring(3, 5)) / 30);

        // 現在bin か 開始bin の遅い方を開始targetDate,binとしてキャンセルAPIを呼び出す
        const cancelStartTime = getJSTTime(
          event.start < now ? now : event.start,
          "datetime"
        ); // '2023-04-17T16:27'
        const bin = toBin(cancelStartTime.substring(11, 16));
        const targetDate = cancelStartTime.substring(0, 10);

        if (event.officeId && event.vehicleCode) {
          const { vehicleCode, officeId } = event;
          cancelChargePlansAPI({ officeId, vehicleCode, targetDate, bin }).then(
            (_) => handleSearchData(searchData)
          );
        } else {
          console.log(
            `invalid params: officeId:${event.officeId}, vehicleCode:${event.vehicleCode
            }, targetDate:${event.targetDate || ""}`
          );
        }
      }
    },
    [searchData, handleSearchData]
  );

  // 検索ボタンのカーソル上下キーで、営業所選択の AutoCompleteSearch の selectedOfficeをひとつ上下に移動する
  const searchButtonKeyDownHandler: KeyboardEventHandler<HTMLButtonElement> = (
    e
  ) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const index = offices.findIndex((o) => o.id === officeSelected?.id);
      if (e.key === "ArrowLeft" && index > 0) {
        onChangeOffice(offices[index - 1]);
      } else if (e.key === "ArrowRight" && index < offices.length - 1) {
        onChangeOffice(offices[index + 1]);
      }
      e.stopPropagation();
    }
  };

  const components: GxBigCalendarHeader = {
    toolbar: () => null,
  } as const;

  // First load
  useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cookieの営業所IDが営業所リストに存在した場合
  useEffect(() => {
    if (initSearchFlag) {
      handleSearchData({ ...searchData });
      setInitSearchFlag(false);
    }
  }, [initSearchFlag, handleSearchData, searchData]);

  useEffect(() => {
    getLoginUserAPI()
      .then((res) => {
        setIsGxAdmin(res.data.role === GX_ADMIN);
      })
      .catch((e) =>
        console.log("==Error occurred while getting userInfo==", e)
      );
  });

  return (
    <Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{ChargePlansConstants.ChargePlans}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "auto",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1} className="required">
                  <AutoCompleteSearch
                    options={offices}
                    autoHighlight
                    getOptionLabel={getOfficeOptionLabel}
                    value={officeSelected || undefined}
                    onChange={(e, value) => {
                      onChangeOffice(value || undefined);
                    }}
                    label={ChargePlansConstants.OfficeName}
                    error={searchDataValidator.officeId}
                  />
                </Grid>
                <Grid item xs={3} md={3} lg={3.1}>
                  <TextFieldForSearch
                    label={ChargePlansConstants.RadioNumber}
                    value={searchData.vehicleCode || ""}
                    setValue={onChangeVehicleCode}
                  />
                </Grid>
                <Grid item xs={12} xl={2.8} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onKeyDown={searchButtonKeyDownHandler}
                    onClickBtn={() => handleSearchData({ ...searchData })}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    onClickBtn={onClickResetSearchData}
                    sx={{ ml: 2 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* データエリア */}
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                // chargePlanCalendarResource.flatMapで増やしたSoC分の列に対するstyle指定
                "& .rbc-time-header-content:nth-of-type(odd)": socResourceStyle,
                "& .rbc-time-header-content:nth-of-type(odd) .rbc-row": { height: "100%", alignItems: "end" },
                "& .rbc-time-header-content:nth-of-type(odd) .rbc-header": {
                  ...socResourceStyle, padding: 0, fontSize: "0.8rem", fontWeight: "normal"
                },
                "& .rbc-time-content .rbc-day-slot:nth-of-type(odd)": socResourceStyle,
                margin: 0,
              }}
            >
              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showSoC}
                        onChange={(e) => setShowSoC(e.target.checked)}
                      />
                    }
                    label={ChargePlansConstants.SoC}
                  />
                </FormGroup>
              </Grid>
              <GxBigCalendar
                targetDateSelected={targetDateSelected}
                events={filteredEvents}
                resources={chargePlans?.chargePlanCalendarResource}
                onChangeDate={onChangeDate}
                onEventSelected={isGxAdmin ? handleSelectEvent : undefined}
                eventPropGetter={chargePlansEventPropGetter}
                components={components}
                tooltipAccessor={(event) =>
                  `${event.title || ""}${event.socStart != null && event.socEnd != null ? `\nSoC: ${event.socStart} - ${event.socEnd}%` : ""}`}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};
